/*
 * In-house cart upsell ("frequently bought together").
 *
 * Self-contained custom element — no edits to cart-drawer-enhancements.js
 * required. It:
 *   - lazily fetches recommendations (Search & Discovery intent=complementary,
 *     falling back to intent=related) for the most-expensive eligible cart line,
 *     NEVER blocking drawer first paint and never during an optimistic add;
 *   - excludes products already in cart + the free/mystery gift products;
 *   - resolves a variant per card from embedded JSON (colour + size);
 *   - ADD reuses the existing fast-add path: cartEnhancements.beginAdd() →
 *     FormData POST /cart/add.js with sections=cart-drawer (the fetch
 *     interceptor rewrites it to cart-drawer-items) → cart.renderContents()
 *     → rollbackOptimistic() on failure. Exactly mirrors product-form.js.
 *
 * Survives section swaps automatically: it lives OUTSIDE
 * .drawer__cart-items-wrapper, and connectedCallback re-hydrates after any
 * full innerHTML re-render. It re-keys on the `cart_changed` event.
 */
(function () {
  "use strict";

  function enh() {
    return window.cartEnhancements || window.cartDrawerEnhancements || null;
  }

  // Lightweight debug logging — enable by adding ?debug to the URL.
  var UPSELL_DEBUG = false;
  try { UPSELL_DEBUG = /[?&]debug(?:=|&|$)/.test(window.location.search || ""); } catch (_) {}

  // We fetch a larger candidate pool and then display only DISPLAY_LIMIT cards
  // that survive the in-cart/gift exclusions — so the upsell reliably shows 2
  // genuinely new products instead of collapsing when recs overlap the cart.
  var FETCH_LIMIT = 6;
  function _ulog() {
    if (!UPSELL_DEBUG) return;
    try { console.log.apply(console, ["[cart-upsell]"].concat([].slice.call(arguments))); } catch (_) {}
  }

  class CartUpsellCarousel extends HTMLElement {
    constructor() {
      super();
      this._hydratedFor = null; // product_id last rendered for
      this._pending = false;
      this._io = null;
      this._ccTimer = null;
      this._addedPids = new Set(); // products added via the upsell — stay excluded
      this._onCartChanged = this._onCartChanged.bind(this);
    }

    connectedCallback() {
      this._bindCards();
      this._runExclusionsAndReveal();
      this._armLazyHydrate();
      // CartWatcher's `cart_changed` is deliberately silenced for every
      // fast-add (see skipNextAdd/silent in emitCartChanges) — it never
      // fires for the normal add-to-cart path. `cart:updated` is what
      // actually fires on every fast-add render (silent or not), so listen
      // to both: cart_changed for external mutations (Visually.io, admin),
      // cart:updated for the common fast-add case.
      window.addEventListener("cart_changed", this._onCartChanged);
      window.addEventListener("cart:updated", this._onCartChanged);
    }

    disconnectedCallback() {
      window.removeEventListener("cart_changed", this._onCartChanged);
      window.removeEventListener("cart:updated", this._onCartChanged);
      if (this._io) { try { this._io.disconnect(); } catch (_) {} this._io = null; }
      clearTimeout(this._ccTimer);
    }

    get _displayLimit() {
      return parseInt(this.getAttribute("data-limit"), 10) || 2;
    }
    get _sectionId() {
      return this.getAttribute("data-section-id") || "cart-upsell";
    }

    _armLazyHydrate() {
      // NOTE: an IntersectionObserver can't be used here — the host starts
      // `hidden` (display:none), so it never reports as intersecting.
      //
      // cartEnhancements.lastCart is a poor signal to wait on here: on
      // boot it's staged as a Liquid-derived stub (items: []) and the real
      // /cart.js is deliberately deferred behind a requestIdleCallback
      // there — a trade-off that's fine for the progress bar/bubble but
      // means `_keyProductId()` can't resolve for many hundreds of ms.
      // Rather than polling for that staged rollout to finish, fire our
      // own /cart.js immediately (one extra request on load, but it runs
      // in parallel with everything else and gets us real items[] as fast
      // as the network allows). If cartEnhancements already has a real
      // cart by the time we check, use it and skip the extra fetch.
      const pid = this._keyProductId();
      if (pid) {
        _ulog("keyed product", pid, "→ hydrating immediately");
        this.hydrate(false);
        return;
      }
      this._fetchOwnCart();
    }

    async _fetchOwnCart() {
      try {
        const fetchFn = window._originalFetch || window.fetch;
        const url = ((window.routes && window.routes.cart_url) || "/cart") + ".js";
        const resp = await fetchFn(url);
        if (!resp.ok) return;
        const cart = await resp.json();
        this._ownCart = cart;
        if (this._hydratedFor != null) return; // a cart_changed event beat us to it
        const pid = this._keyProductId();
        if (pid) {
          _ulog("own /cart.js fetch keyed product", pid, "→ hydrating");
          this.hydrate(false);
        }
      } catch (e) {
        _ulog("own /cart.js fetch failed", e);
      }
    }

    _keyProductId() {
      const ce = enh();
      let cart = ce && ce.lastCart;
      if (!cart || !Array.isArray(cart.items) || !cart.items.length) cart = this._ownCart;
      if (!cart || !Array.isArray(cart.items) || !cart.items.length) return null;
      const eligible = cart.items.filter((it) => {
        const t = it.product_type || "";
        if (t === "Hidden" || t === "Bundle" || t === "Taxes") return false;
        const p = it.properties || {};
        if (p._free_gift === "true" || p._mystery_gift === "true") return false;
        return true;
      });
      if (!eligible.length) return null;
      // Default: most-expensive eligible line (stable anchor purchase).
      let chosen = eligible[0];
      for (let i = 1; i < eligible.length; i++) {
        const a = eligible[i].line_price || eligible[i].price || 0;
        const b = chosen.line_price || chosen.price || 0;
        if (a > b) chosen = eligible[i];
      }
      return chosen.product_id || null;
    }

    _recUrl(productId, intent) {
      const base =
        (window.routes && window.routes.product_recommendations_url) ||
        "/recommendations/products";
      return (
        base +
        "?section_id=" + encodeURIComponent(this._sectionId) +
        "&product_id=" + encodeURIComponent(productId) +
        "&intent=" + encodeURIComponent(intent) +
        "&limit=" + FETCH_LIMIT
      );
    }

    async _fetchCards(productId, intent) {
      const fetchFn = window._originalFetch || window.fetch;
      const url = this._recUrl(productId, intent);
      try {
        const resp = await fetchFn(url);
        if (!resp.ok) {
          _ulog("fetch failed", intent, resp.status, url);
          return null;
        }
        const text = await resp.text();
        const tmp = document.createElement("div");
        tmp.innerHTML = text;
        const fresh = tmp.querySelector("cart-upsell-carousel");
        if (!fresh) {
          _ulog("no <cart-upsell-carousel> in response for", intent, "(check section_id=cart-upsell exists)");
          return null;
        }
        const count = fresh.querySelectorAll(".cart-upsell__card").length;
        _ulog(intent, "→", count, "cards");
        return count ? fresh.innerHTML : "";
      } catch (e) {
        _ulog("fetch error", intent, e);
        return null;
      }
    }

    async hydrate(force) {
      const ce = enh();
      if (ce && ce._optimisticAddInFlight) return; // never during optimistic add
      const pid = this._keyProductId();
      if (!pid) { this.setAttribute("hidden", ""); return; }
      if (
        !force &&
        this._hydratedFor === pid &&
        this.querySelector(".cart-upsell__card")
      ) {
        this._runExclusionsAndReveal();
        return;
      }
      if (this._pending) return;
      this._pending = true;
      try {
        // Fire complementary (Search & Discovery) and related IN PARALLEL so we
        // don't pay two sequential ~1s section renders. Both requests are
        // in flight at once, but we only AWAIT the fallback if the primary
        // comes back empty — no reason to hold the primary's cards hostage
        // to whichever of the two happens to be slower.
        const intent = this.getAttribute("data-intent") || "complementary";
        const primaryPromise = this._fetchCards(pid, intent);
        const fallbackPromise = this._fetchCards(pid, "related");
        let html = await primaryPromise;
        if (!html) html = (await fallbackPromise) || "";
        if (html) {
          this.innerHTML = html;
          this._hydratedFor = pid;
          this._bindCards();
          this._runExclusionsAndReveal();
        } else {
          this.setAttribute("hidden", "");
        }
      } finally {
        this._pending = false;
      }
    }

    _onCartChanged() {
      clearTimeout(this._ccTimer);
      this._ccTimer = setTimeout(() => {
        // The upsell no longer depends on cart contents (we don't exclude
        // in-cart items), so cart changes are INSTANT — no re-fetch, no
        // innerHTML swap, no jank. Just:
        //   - hydrate once if we never did (e.g. cart was empty on load), or
        //   - re-run the cheap reveal and only re-fetch if the pool ran dry.
        if (this._hydratedFor == null) {
          const pid = this._keyProductId();
          if (pid) { this.hydrate(false); return; }
          // ce.lastCart.items is deliberately stale right after a fast-add
          // (_applyFastAddRender flags it _itemsStale — it can't know the
          // full item list from a partial /cart/add.js response), so on the
          // very first add from an empty cart _keyProductId finds nothing
          // even though cart:updated just fired. Fetch a fresh /cart.js
          // ourselves (same fallback _armLazyHydrate uses on initial
          // connect) instead of silently giving up until the next change.
          this._fetchOwnCart();
          return;
        }
        const shown = this._runExclusionsAndReveal();
        if (shown === 0 && this._keyProductId()) this.hydrate(true);
      }, 120);
    }

    _cardVariants(card) {
      const tag = card.querySelector(".cart-upsell__variants");
      if (!tag) return [];
      try { return JSON.parse(tag.textContent) || []; } catch (_) { return []; }
    }

    // Colour → image URL map embedded in the card at fetch time (see
    // cart-upsell-carousel.liquid) — swapping the thumbnail on swatch click
    // reads this instead of refetching the product.
    _cardImages(card) {
      if (card._imageMap !== undefined) return card._imageMap;
      const tag = card.querySelector(".cart-upsell__images");
      let map = null;
      if (tag) {
        try { map = JSON.parse(tag.textContent) || null; } catch (_) { map = null; }
      }
      card._imageMap = map;
      return map;
    }

    _updateImage(card, colorHandle) {
      if (!colorHandle) return;
      const images = this._cardImages(card);
      if (!images) return;
      const url = images[colorHandle];
      if (!url) return;
      const img = card.querySelector(".cart-upsell__card-img");
      if (!img || img.dataset.currentColor === colorHandle) return;
      img.dataset.currentColor = colorHandle;
      // Drop srcset/sizes — they still point at the previous colour's widths
      // and would win over a plain `src` swap in a responsive-image picker.
      img.removeAttribute("srcset");
      img.removeAttribute("sizes");
      img.src = url;
    }

    _runExclusionsAndReveal() {
      const cfg = window.CART_ENHANCEMENT_CONFIG || {};
      const giftVariantIds = new Set([
        String(cfg.FREE_GIFT_VARIANT_ID || ""),
        String(cfg.MYSTERY_GIFT_VARIANT_ID || ""),
      ]);
      const cards = this.querySelectorAll(".cart-upsell__card");
      const max = this._displayLimit;
      let shown = 0;
      let excludedAdded = 0;
      let excludedGift = 0;
      cards.forEach((card) => {
        let hide = false;
        const pid = card.getAttribute("data-product-id");
        // Only roll off products the user added via THIS upsell (so the next
        // candidate appears). Products already in the cart are still shown and
        // remain addable — the Add button is never blocked for them.
        if (pid && this._addedPids.has(String(pid))) { hide = true; excludedAdded++; }
        if (!hide) {
          const variants = this._cardVariants(card);
          if (variants.some((v) => giftVariantIds.has(String(v.id)))) { hide = true; excludedGift++; }
        }
        // Cap the visible cards to the display limit (2) — we fetched a larger
        // pool so enough survive to fill those slots.
        if (!hide && shown >= max) hide = true;
        card.hidden = hide;
        if (!hide) shown++;
      });
      _ulog(
        "reveal:", cards.length, "fetched,", shown, "shown (cap " + max + "),",
        excludedAdded, "added-via-upsell,", excludedGift, "gift"
      );
      if (shown > 0) this.removeAttribute("hidden");
      else this.setAttribute("hidden", "");
      return shown;
    }

    _bindCards() {
      this.querySelectorAll(".cart-upsell__card").forEach((card) => {
        if (card._upsellBound) return;
        card._upsellBound = true;

        const variants = this._cardVariants(card);
        const state = { color: null, size: null };

        const swatches = card.querySelectorAll(".cart-upsell__swatch");
        if (swatches.length) {
          const selected = card.querySelector(".cart-upsell__swatch.is-selected") || swatches[0];
          state.color = selected.getAttribute("data-color");
          this._updateImage(card, state.color);
          swatches.forEach((sw) => {
            sw.addEventListener("click", () => {
              swatches.forEach((s) => {
                s.classList.remove("is-selected");
                s.setAttribute("aria-pressed", "false");
              });
              sw.classList.add("is-selected");
              sw.setAttribute("aria-pressed", "true");
              state.color = sw.getAttribute("data-color");
              this._updateImage(card, state.color);
              this._resolveVariant(card, variants, state);
            });
          });
        }

        const sizeSel = card.querySelector(".cart-upsell__size");
        if (sizeSel) {
          // Defaults to the "Select a size" placeholder (value ""), so the
          // customer must pick a size before the card is addable.
          state.size = sizeSel.value;
          sizeSel.addEventListener("change", (e) => {
            // The carousel is rendered INSIDE <cart-drawer-items>, whose
            // delegated `change` handler (cart.js) treats any bubbled change as
            // a line-item quantity edit and fires /cart/update.js — which
            // re-renders the section and makes this carousel flicker. Keep the
            // size change local to the upsell.
            e.stopPropagation();
            state.size = sizeSel.value;
            sizeSel.classList.remove("is-invalid");
            this._resolveVariant(card, variants, state);
          });
        }

        this._resolveVariant(card, variants, state);

        const addBtn = card.querySelector(".cart-upsell__add");
        if (addBtn) {
          addBtn.addEventListener("click", () => this._onAdd(card, addBtn));
        }
      });
    }

    // Mark out-of-stock sizes (for the selected colour) in the dropdown and,
    // if the current selection is sold out, switch to the first in-stock size.
    _updateSizeAvailability(card, variants, state) {
      const sizeSel = card.querySelector(".cart-upsell__size");
      if (!sizeSel) return;
      Array.from(sizeSel.options).forEach((opt) => {
        if (opt.value === "") return; // leave the "Select a size" placeholder as-is
        const v = variants.find(
          (vr) =>
            (!state.color || !vr.color || vr.color === state.color) &&
            vr.size === opt.value
        );
        const available = v ? v.available !== false : false;
        if (opt._baseLabel == null) opt._baseLabel = opt.textContent;
        opt.disabled = !available;
        opt.textContent = available ? opt._baseLabel : opt._baseLabel + " — Sold out";
      });
      // If a CHOSEN size is now sold out for this colour, move to the first
      // available one so the selection is always addable. Never override the
      // "Select a size" placeholder (empty value) — the customer must pick.
      const cur = sizeSel.selectedOptions && sizeSel.selectedOptions[0];
      if (cur && cur.value !== "" && cur.disabled) {
        const firstAvail = Array.from(sizeSel.options).find((o) => o.value !== "" && !o.disabled);
        if (firstAvail) {
          sizeSel.value = firstAvail.value;
          state.size = firstAvail.value;
        }
      }
    }

    _resolveVariant(card, variants, state) {
      const addBtn = card.querySelector(".cart-upsell__add");
      if (!variants || !variants.length) return;

      // Reflect per-size stock for the current colour in the dropdown first
      // (may auto-correct state.size to an available one).
      this._updateSizeAvailability(card, variants, state);

      let pool = variants;
      if (state.color) {
        const byColor = pool.filter((v) => !v.color || v.color === state.color);
        if (byColor.length) pool = byColor;
      }

      // Representative variant for price display (first in-stock in the pool).
      const repr = pool.find((v) => v.available !== false) || pool[0];

      // When the card has a size dropdown it defaults to a "Select a size"
      // placeholder — a size MUST be chosen before adding. Show a representative
      // price but leave the card unaddable (no variant id) until then; _onAdd
      // nudges the customer to the dropdown if they click early.
      const sizeSel = card.querySelector(".cart-upsell__size");
      if (sizeSel && !state.size) {
        card._selectedVariant = null;
        if (addBtn) addBtn.removeAttribute("data-variant-id");
        if (repr) this._renderPrice(card, repr);
        return;
      }

      let match = null;
      if (state.size) {
        match =
          pool.find((v) => v.size === state.size && v.available !== false) ||
          pool.find((v) => v.size === state.size);
      }
      if (!match) {
        match = repr;
      }

      card._selectedVariant = match || null;
      if (addBtn && match) {
        // Never block the Add button on availability — resolution already
        // prefers an in-stock variant; if an add ever fails it just rolls back.
        addBtn.setAttribute("data-variant-id", match.id);
      }
      if (match) this._renderPrice(card, match);
    }

    // Format cents the same way global.js does (Shopify.formatMoney when
    // available, $X.XX fallback) so the upsell price matches the cart line item.
    _formatMoney(cents) {
      if (typeof cents === "string") cents = parseInt(cents, 10);
      try {
        if (window.Shopify && typeof window.Shopify.formatMoney === "function") {
          return window.Shopify.formatMoney(
            cents,
            (window.theme && window.theme.moneyFormat) || "${{amount}}"
          );
        }
      } catch (_) {}
      return "$" + (cents / 100).toFixed(2);
    }

    // Rebuild the price block for the resolved variant — sale (red) +
    // strikethrough compare-at when on sale, else a single regular price.
    // Mirrors the cart line item markup in cart-drawer-items.liquid.
    _renderPrice(card, variant) {
      const wrap = card.querySelector(".cart-upsell__card-price");
      if (!wrap || typeof variant.price !== "number") return;
      const compare = typeof variant.compare_at === "number" ? variant.compare_at : 0;
      if (compare && compare > variant.price) {
        wrap.innerHTML =
          '<span class="cart-upsell__price-sale">' + this._formatMoney(variant.price) + "</span>" +
          '<span class="cart-upsell__price-compare-at"><s>' + this._formatMoney(compare) + "</s></span>";
      } else {
        wrap.innerHTML =
          '<span class="cart-upsell__price-regular">' + this._formatMoney(variant.price) + "</span>";
      }
    }

    _previewItem(card) {
      const img = card.querySelector(".cart-upsell__card-img");
      const titleEl = card.querySelector(".cart-upsell__card-title");
      const sizeSel = card.querySelector(".cart-upsell__size");
      const swatch = card.querySelector(".cart-upsell__swatch.is-selected");
      const v = card._selectedVariant || null;
      return {
        title: titleEl ? titleEl.textContent.trim() : "",
        image: img ? img.currentSrc || img.src : null,
        variant_title: sizeSel ? sizeSel.value : "",
        options: swatch
          ? [{ name: "Colour", value: swatch.getAttribute("aria-label") || "" }]
          : null,
        price: v && typeof v.price === "number" ? v.price : null,
      };
    }

    async _onAdd(card, addBtn) {
      const ce = enh();
      const drawer = document.querySelector("cart-drawer");
      if (addBtn.disabled) return;
      const variantId = addBtn.getAttribute("data-variant-id");
      if (!variantId) {
        // No size chosen yet (dropdown still on "Select a size") — guide the
        // customer to the size selector instead of silently doing nothing.
        const sizeSel = card.querySelector(".cart-upsell__size");
        if (sizeSel) {
          sizeSel.classList.add("is-invalid");
          // preventScroll: focusing otherwise scrolls the select into view,
          // which yanks the drawer/page upward (very jarring inside the drawer).
          try { sizeSel.focus({ preventScroll: true }); } catch (_) {}
        }
        return;
      }

      // Optimistic preview — mirrors product-form.js.
      try {
        if (ce && typeof ce.beginAdd === "function") {
          ce.beginAdd(1, [this._previewItem(card)]);
        }
      } catch (_) {}

      // Loading state — "Adding…" with a pulse (see .cart-upsell__add.is-loading).
      const originalLabel = addBtn.textContent;
      addBtn.classList.add("is-loading");
      addBtn.disabled = true;
      addBtn.textContent = "Adding…";

      const fd = new FormData();
      fd.append("id", variantId);
      fd.append("quantity", "1");
      // Tag the line item as added via the cart upsell (hidden `_`-prefixed
      // properties) so it's identifiable in the cart object / analytics —
      // `_upsell` marks any upsell-driven add, `_upsell_source` pins down
      // which upsell surface it came from (this carousel).
      fd.append("properties[_upsell]", "true");
      fd.append("properties[_upsell_source]", "cart-upsell");
      // sections=cart-drawer → the fast-add interceptor rewrites it to
      // cart-drawer-items, marking _fastAddPending for the patched renderContents.
      fd.append("sections", "cart-drawer");

      const addUrl = (window.routes && window.routes.cart_add_url) || "/cart/add";

      let ok = false;
      try {
        const resp = await window.fetch(addUrl, {
          method: "POST",
          headers: { Accept: "application/json" },
          body: fd,
        });
        const data = await resp.json().catch(() => ({}));
        if (resp.ok && !(data && data.status)) {
          ok = true;
          // Drive the same render path product-form.js uses.
          if (drawer && typeof drawer.renderContents === "function") {
            drawer.renderContents(data);
          }
        } else if (ce && typeof ce.rollbackOptimistic === "function") {
          try { ce.rollbackOptimistic(); } catch (_) {}
        }
      } catch (e) {
        if (ce && typeof ce.rollbackOptimistic === "function") {
          try { ce.rollbackOptimistic(); } catch (_) {}
        }
      }

      if (ok) {
        // Remove the just-added card and surface the next candidate from the
        // fetched pool immediately — don't wait for the cart_changed round-trip.
        // Track the id so it stays excluded even before lastCart reflects it.
        const pid = card.getAttribute("data-product-id");
        if (pid) this._addedPids.add(String(pid));
        addBtn.textContent = "Added";
        this._runExclusionsAndReveal();
      } else {
        // Restore so the customer can retry.
        addBtn.classList.remove("is-loading");
        addBtn.disabled = false;
        addBtn.textContent = originalLabel;
      }
    }
  }

  if (!customElements.get("cart-upsell-carousel")) {
    customElements.define("cart-upsell-carousel", CartUpsellCarousel);
  }
})();
